import React, { forwardRef, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import _isEqual from 'lodash/isEqual';

import type { Node } from 'react';
import usePrevious from '@src/hooks/usePrevious';
import type { ReactRefT } from '@src/types/misc';

type TLandingPadProps = {
  htmlId: string;
};

type TPortalProps = {
  children: Node;
  htmlId: string;
  style?: Record<string, string | number>;
};

export function LandingPad({ htmlId }: TLandingPadProps) {
  return <div id={htmlId} />;
}

export const Portal = forwardRef(function Portal(
  { children, htmlId, style }: TPortalProps,
  ref?: ReactRefT<typeof HTMLDivElement>
) {
  if (typeof document === 'undefined') return null;

  return (
    <ClientPortal htmlId={htmlId} style={style} ref={ref}>
      {children}
    </ClientPortal>
  );
});

const ClientPortal = forwardRef(function ClientPortal(
  { children, htmlId, style }: TPortalProps,
  ref?: ReactRefT<typeof HTMLDivElement>
) {
  const elRef = useRef(document.createElement('div'));
  const rootRef = useRef(null);

  const prevStyle = usePrevious(style);

  if (ref) ref.current = elRef.current;

  useEffect(() => {
    rootRef.current = document && document.getElementById(htmlId);
    rootRef.current && rootRef.current.appendChild(elRef.current);

    return () => {
      rootRef.current && rootRef.current.removeChild(elRef.current);
    };
  }, [htmlId]);

  useEffect(() => {
    if (_isEqual(style, prevStyle)) return;

    $(elRef.current).css(style);
  }, [prevStyle, style]);

  return ReactDOM.createPortal(children, elRef.current);
});

LandingPad.defaultProps = {
  htmlId: 'portal-root'
};

Portal.defaultProps = {
  htmlId: 'portal-root'
};
